import React, { useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';

// AI Feature detailed information with benefits, limitations, and use cases
const featureDetails = {
  'simplify-notice': {
    name: { en: 'Notice Simplifier', hi: 'नोटिस सरलकर्ता' },
    icon: '📝',
    category: 'admin',
    shortDesc: { 
      en: 'Convert complex notices to WhatsApp-friendly format', 
      hi: 'जटिल नोटिस को WhatsApp के लिए सरल बनाएं' 
    },
    fullDesc: {
      en: 'This AI feature takes complex school circulars, notices, and official communications and converts them into simple, easy-to-understand messages suitable for WhatsApp sharing with parents.',
      hi: 'यह AI सुविधा जटिल स्कूल परिपत्रों, नोटिस और आधिकारिक संचार को सरल, आसानी से समझ में आने वाले संदेशों में बदल देती है जो माता-पिता के साथ WhatsApp पर साझा करने के लिए उपयुक्त हैं।'
    },
    howItWorks: {
      en: [
        'Paste your original notice or circular',
        'Select the output format (WhatsApp, Bullet Points, or Detailed)',
        'AI analyzes the content and extracts key information',
        'Generates a simplified version in Hindi/Hinglish'
      ],
      hi: [
        'अपना मूल नोटिस या परिपत्र पेस्ट करें',
        'आउटपुट फॉर्मेट चुनें (WhatsApp, बुलेट पॉइंट्स, या विस्तृत)',
        'AI सामग्री का विश्लेषण करता है और मुख्य जानकारी निकालता है',
        'हिंदी/हिंग्लिश में सरलीकृत संस्करण तैयार करता है'
      ]
    },
    benefits: {
      en: [
        'Saves time in manual rewriting',
        'Ensures important dates and deadlines are highlighted',
        'Uses language that all parents can understand',
        'Maintains consistency in communication'
      ],
      hi: [
        'मैन्युअल पुनर्लेखन में समय बचाता है',
        'सुनिश्चित करता है कि महत्वपूर्ण तिथियां और समय-सीमा हाइलाइट हों',
        'ऐसी भाषा का उपयोग करता है जो सभी माता-पिता समझ सकें',
        'संचार में एकरूपता बनाए रखता है'
      ]
    },
    limitations: {
      en: [
        'May not capture very specific school policies',
        'Requires review before sending',
        'Best for routine communications, not legal notices'
      ],
      hi: [
        'बहुत विशिष्ट स्कूल नीतियों को capture नहीं कर सकता',
        'भेजने से पहले समीक्षा आवश्यक है',
        'नियमित संचार के लिए सर्वोत्तम, कानूनी नोटिस के लिए नहीं'
      ]
    },
    useCases: {
      en: ['PTM notifications', 'Exam schedules', 'Holiday announcements', 'Fee reminders', 'Event invitations'],
      hi: ['PTM सूचनाएं', 'परीक्षा कार्यक्रम', 'छुट्टी की घोषणाएं', 'शुल्क रिमाइंडर', 'कार्यक्रम आमंत्रण']
    }
  },
  'progress-summary': {
    name: { en: 'Progress Summary Generator', hi: 'प्रगति सारांश जनरेटर' },
    icon: '📊',
    category: 'teacher',
    shortDesc: { 
      en: 'Generate parent-friendly student progress reports', 
      hi: 'माता-पिता के अनुकूल विद्यार्थी प्रगति रिपोर्ट बनाएं' 
    },
    fullDesc: {
      en: 'Creates comprehensive yet easy-to-understand progress summaries for students that highlight strengths, areas for improvement, and actionable suggestions for parents.',
      hi: 'छात्रों के लिए व्यापक लेकिन समझने में आसान प्रगति सारांश बनाता है जो ताकत, सुधार के क्षेत्रों और माता-पिता के लिए कार्रवाई योग्य सुझावों को उजागर करता है।'
    },
    howItWorks: {
      en: [
        'Enter student name and class',
        'Add subjects and attendance percentage',
        'Optionally include marks summary',
        'AI generates a balanced, encouraging report'
      ],
      hi: [
        'छात्र का नाम और कक्षा दर्ज करें',
        'विषय और उपस्थिति प्रतिशत जोड़ें',
        'वैकल्पिक रूप से अंक सारांश शामिल करें',
        'AI एक संतुलित, प्रोत्साहक रिपोर्ट तैयार करता है'
      ]
    },
    benefits: {
      en: [
        'Saves hours of report writing',
        'Maintains positive and encouraging tone',
        'Provides actionable suggestions',
        'Professional quality reports'
      ],
      hi: [
        'रिपोर्ट लिखने के घंटों की बचत',
        'सकारात्मक और प्रोत्साहक टोन बनाए रखता है',
        'कार्रवाई योग्य सुझाव प्रदान करता है',
        'पेशेवर गुणवत्ता वाली रिपोर्ट'
      ]
    },
    limitations: {
      en: [
        'Based on provided data only',
        'Cannot assess behavioral aspects without input',
        'Generic suggestions without specific student history'
      ],
      hi: [
        'केवल प्रदान किए गए डेटा पर आधारित',
        'इनपुट के बिना व्यवहारिक पहलुओं का आकलन नहीं कर सकता',
        'विशिष्ट छात्र इतिहास के बिना सामान्य सुझाव'
      ]
    },
    useCases: {
      en: ['PTM reports', 'Monthly progress cards', 'Term-end summaries', 'Special achievement recognition'],
      hi: ['PTM रिपोर्ट', 'मासिक प्रगति कार्ड', 'सत्र-अंत सारांश', 'विशेष उपलब्धि मान्यता']
    }
  },
  'homework-hint': {
    name: { en: 'Homework Hints (Story Mode + Image)', hi: 'होमवर्क हिंट्स (कहानी मोड + इमेज)' },
    icon: '💡',
    category: 'student',
    shortDesc: { 
      en: 'Get hints with comic characters - now with image upload!', 
      hi: 'कॉमिक कैरेक्टर के साथ हिंट्स - अब फोटो भी upload करें!' 
    },
    fullDesc: {
      en: 'A unique learning assistant that provides homework hints through engaging stories featuring beloved Indian comic characters like Chacha Chaudhary, Sabu, Billu, and Pinki. NEW: You can now upload photos of your notebook, textbook, or any problem - the AI will see and explain it! Instead of giving direct answers, it guides students to find solutions themselves through fun narratives.',
      hi: 'एक अनूठा लर्निंग असिस्टेंट जो चाचा चौधरी, साबू, बिल्लू और पिंकी जैसे प्रिय भारतीय कॉमिक पात्रों की आकर्षक कहानियों के माध्यम से होमवर्क हिंट्स प्रदान करता है। नया: अब आप अपनी notebook, किताब, या किसी भी समस्या की फोटो upload कर सकते हैं - AI उसे देखकर समझाएगा! सीधे उत्तर देने के बजाय, यह मज़ेदार कहानियों के माध्यम से छात्रों को स्वयं समाधान खोजने में मार्गदर्शन करता है।'
    },
    howItWorks: {
      en: [
        'Upload a photo of your problem (optional but recommended!)',
        'Enter your subject and topic',
        'Type your question or just write "see the image"',
        'Select your class level',
        'Choose regular hints or Story Mode with comic characters',
        'AI sees your image and gives personalized hints!'
      ],
      hi: [
        'अपनी समस्या की फोटो upload करें (वैकल्पिक लेकिन recommended!)',
        'अपना विषय और टॉपिक दर्ज करें',
        'अपना प्रश्न टाइप करें या सिर्फ "image में देखो" लिखें',
        'अपनी कक्षा का स्तर चुनें',
        'नियमित हिंट्स या कॉमिक कैरेक्टर के साथ स्टोरी मोड चुनें',
        'AI आपकी image देखकर personalized hints देगा!'
      ]
    },
    benefits: {
      en: [
        'Upload photos of handwritten problems, diagrams, graphs',
        'AI understands images and explains visually',
        'Makes learning fun and engaging with comic characters',
        'Encourages independent thinking',
        'Builds problem-solving skills',
        'Works with Math, Science, Hindi, English - any subject!'
      ],
      hi: [
        'हस्तलिखित समस्याओं, diagrams, graphs की फोटो upload करें',
        'AI images को समझता है और visually समझाता है',
        'कॉमिक पात्रों के साथ सीखना मज़ेदार बनाता है',
        'स्वतंत्र सोच को प्रोत्साहित करता है',
        'समस्या-समाधान कौशल विकसित करता है',
        'Math, Science, Hindi, English - किसी भी विषय में काम करता है!'
      ]
    },
    limitations: {
      en: [
        'Does NOT provide complete answers',
        'Image should be clear and well-lit',
        'Max image size: 5MB',
        'Supported formats: JPEG, PNG, WebP',
        'Story mode is for engagement, not for exam prep'
      ],
      hi: [
        'पूर्ण उत्तर प्रदान नहीं करता',
        'Image स्पष्ट और अच्छी रोशनी में होनी चाहिए',
        'अधिकतम image size: 5MB',
        'समर्थित formats: JPEG, PNG, WebP',
        'स्टोरी मोड engagement के लिए है, परीक्षा तैयारी के लिए नहीं'
      ]
    },
    useCases: {
      en: ['Math problem solving', 'Science concepts', 'Language exercises', 'Social studies topics'],
      hi: ['गणित समस्या समाधान', 'विज्ञान अवधारणाएं', 'भाषा अभ्यास', 'सामाजिक अध्ययन विषय']
    },
    comicCharacters: {
      'chacha': { name: 'चाचा चौधरी', desc: 'बुद्धिमान बुज़ुर्ग जो हर समस्या का हल जानते हैं', trait: 'wisdom' },
      'sabu': { name: 'साबू', desc: 'जुपिटर का शक्तिशाली योद्धा, चाचा का साथी', trait: 'strength' },
      'billu': { name: 'बिल्लू', desc: 'शरारती और चतुर लड़का जो हमेशा नई चीज़ें सीखता है', trait: 'curiosity' },
      'pinki': { name: 'पिंकी', desc: 'समझदार और होशियार लड़की जो हमेशा मदद करती है', trait: 'helpfulness' }
    }
  },
  'generate-mcq': {
    name: { en: 'MCQ Generator', hi: 'MCQ जनरेटर' },
    icon: '❓',
    category: 'student',
    shortDesc: { 
      en: 'Generate practice MCQs with explanations', 
      hi: 'व्याख्या के साथ अभ्यास MCQs बनाएं' 
    },
    fullDesc: {
      en: 'Automatically generates Multiple Choice Questions with detailed explanations for any subject and topic. Perfect for exam preparation and self-assessment.',
      hi: 'किसी भी विषय और टॉपिक के लिए विस्तृत व्याख्या के साथ स्वचालित रूप से बहुविकल्पीय प्रश्न तैयार करता है। परीक्षा की तैयारी और आत्म-मूल्यांकन के लिए एकदम सही।'
    },
    howItWorks: {
      en: [
        'Select subject and enter topic',
        'Choose class level and difficulty',
        'Set number of questions (1-10)',
        'AI generates quality MCQs with answers and explanations'
      ],
      hi: [
        'विषय चुनें और टॉपिक दर्ज करें',
        'कक्षा स्तर और कठिनाई चुनें',
        'प्रश्नों की संख्या सेट करें (1-10)',
        'AI उत्तर और व्याख्या के साथ गुणवत्ता वाले MCQs तैयार करता है'
      ]
    },
    benefits: {
      en: [
        'Instant question generation',
        'Detailed explanations help in learning',
        'Adjustable difficulty levels',
        'Covers any topic in the curriculum'
      ],
      hi: [
        'तत्काल प्रश्न निर्माण',
        'विस्तृत व्याख्याएं सीखने में मदद करती हैं',
        'समायोज्य कठिनाई स्तर',
        'पाठ्यक्रम में किसी भी विषय को कवर करता है'
      ]
    },
    limitations: {
      en: [
        'May not match exact board exam pattern',
        'Explanations are AI-generated, verify for accuracy',
        'Best used for practice, not official tests'
      ],
      hi: [
        'सटीक बोर्ड परीक्षा पैटर्न से मेल नहीं खा सकता',
        'व्याख्याएं AI-जनित हैं, सटीकता के लिए सत्यापित करें',
        'अभ्यास के लिए सर्वोत्तम, आधिकारिक परीक्षणों के लिए नहीं'
      ]
    },
    useCases: {
      en: ['Daily practice', 'Chapter revision', 'Mock tests', 'Homework assistance'],
      hi: ['दैनिक अभ्यास', 'अध्याय पुनरीक्षण', 'मॉक टेस्ट', 'होमवर्क सहायता']
    }
  },
  'study-plan': {
    name: { en: 'Study Plan Generator', hi: 'स्टडी प्लान जनरेटर' },
    icon: '📅',
    category: 'student',
    shortDesc: { 
      en: 'Create personalized daily study plans', 
      hi: 'व्यक्तिगत दैनिक स्टडी प्लान बनाएं' 
    },
    fullDesc: {
      en: 'Creates a customized study schedule based on available time, subjects to cover, and learning preferences. Helps students manage their study time effectively.',
      hi: 'उपलब्ध समय, कवर किए जाने वाले विषयों और सीखने की प्राथमिकताओं के आधार पर एक अनुकूलित अध्ययन कार्यक्रम बनाता है। छात्रों को अपने अध्ययन समय का प्रभावी ढंग से प्रबंधन करने में मदद करता है।'
    },
    howItWorks: {
      en: [
        'Enter student name and class',
        'List subjects to study',
        'Set available study duration',
        'AI creates a balanced, realistic schedule'
      ],
      hi: [
        'छात्र का नाम और कक्षा दर्ज करें',
        'अध्ययन करने के लिए विषयों की सूची बनाएं',
        'उपलब्ध अध्ययन अवधि सेट करें',
        'AI एक संतुलित, यथार्थवादी कार्यक्रम बनाता है'
      ]
    },
    benefits: {
      en: [
        'Time-efficient study planning',
        'Balanced subject coverage',
        'Includes break suggestions',
        'Adaptable to any schedule'
      ],
      hi: [
        'समय-कुशल अध्ययन योजना',
        'संतुलित विषय कवरेज',
        'ब्रेक सुझाव शामिल',
        'किसी भी शेड्यूल के अनुकूल'
      ]
    },
    limitations: {
      en: [
        'Cannot account for individual learning speed',
        'Generic suggestions without knowing specific syllabus',
        'Needs manual adjustment based on progress'
      ],
      hi: [
        'व्यक्तिगत सीखने की गति का हिसाब नहीं लगा सकता',
        'विशिष्ट पाठ्यक्रम जाने बिना सामान्य सुझाव',
        'प्रगति के आधार पर मैन्युअल समायोजन की आवश्यकता'
      ]
    },
    useCases: {
      en: ['Daily study routine', 'Exam preparation', 'Holiday study plan', 'Balancing multiple subjects'],
      hi: ['दैनिक अध्ययन दिनचर्या', 'परीक्षा की तैयारी', 'छुट्टी अध्ययन योजना', 'कई विषयों में संतुलन']
    }
  },
  'faq': {
    name: { en: 'FAQ Bot', hi: 'FAQ बॉट' },
    icon: '🤖',
    category: 'all',
    shortDesc: { 
      en: 'Get instant answers to school-related questions', 
      hi: 'स्कूल संबंधित प्रश्नों के तुरंत उत्तर पाएं' 
    },
    fullDesc: {
      en: 'An intelligent FAQ assistant that answers common questions about school procedures, policies, timings, and general educational queries in simple Hindi/English.',
      hi: 'एक बुद्धिमान FAQ सहायक जो स्कूल प्रक्रियाओं, नीतियों, समय और सामान्य शैक्षिक प्रश्नों के बारे में सरल हिंदी/अंग्रेजी में सामान्य प्रश्नों का उत्तर देता है।'
    },
    howItWorks: {
      en: [
        'Type your question in Hindi or English',
        'AI understands the context',
        'Provides clear, helpful answer',
        'Can handle follow-up questions'
      ],
      hi: [
        'अपना प्रश्न हिंदी या अंग्रेजी में टाइप करें',
        'AI संदर्भ को समझता है',
        'स्पष्ट, सहायक उत्तर प्रदान करता है',
        'अनुवर्ती प्रश्नों को संभाल सकता है'
      ]
    },
    benefits: {
      en: [
        '24/7 availability',
        'Instant responses',
        'Bilingual support',
        'Reduces staff workload'
      ],
      hi: [
        '24/7 उपलब्धता',
        'तुरंत प्रतिक्रिया',
        'द्विभाषी समर्थन',
        'स्टाफ कार्यभार कम करता है'
      ]
    },
    limitations: {
      en: [
        'Cannot access real-time school data',
        'May not know school-specific policies',
        'For general guidance only'
      ],
      hi: [
        'वास्तविक समय स्कूल डेटा तक पहुंच नहीं सकता',
        'स्कूल-विशिष्ट नीतियां नहीं जान सकता',
        'केवल सामान्य मार्गदर्शन के लिए'
      ]
    },
    useCases: {
      en: ['Admission queries', 'Fee structure questions', 'Timing inquiries', 'General educational guidance'],
      hi: ['प्रवेश प्रश्न', 'शुल्क संरचना प्रश्न', 'समय पूछताछ', 'सामान्य शैक्षिक मार्गदर्शन']
    }
  },
  'fee-reminder': {
    name: { en: 'Fee Reminder Drafter', hi: 'शुल्क रिमाइंडर ड्राफ्टर' },
    icon: '💰',
    category: 'admin',
    shortDesc: { 
      en: 'Draft professional fee reminder messages', 
      hi: 'पेशेवर शुल्क रिमाइंडर संदेश बनाएं' 
    },
    fullDesc: {
      en: 'Creates respectful and professional fee reminder messages with adjustable tones from polite to urgent. Perfect for WhatsApp communication with parents.',
      hi: 'विनम्र से तत्काल तक समायोज्य टोन के साथ सम्मानजनक और पेशेवर शुल्क रिमाइंडर संदेश बनाता है। माता-पिता के साथ WhatsApp संचार के लिए एकदम सही।'
    },
    howItWorks: {
      en: [
        'Enter parent and student details',
        'Add due amount and date',
        'Select message tone',
        'AI generates appropriate reminder'
      ],
      hi: [
        'माता-पिता और छात्र विवरण दर्ज करें',
        'देय राशि और तारीख जोड़ें',
        'संदेश टोन चुनें',
        'AI उचित रिमाइंडर तैयार करता है'
      ]
    },
    benefits: {
      en: [
        'Maintains professional relationship',
        'Consistent messaging',
        'Multiple tone options',
        'WhatsApp ready format'
      ],
      hi: [
        'पेशेवर संबंध बनाए रखता है',
        'सुसंगत मैसेजिंग',
        'कई टोन विकल्प',
        'WhatsApp तैयार फॉर्मेट'
      ]
    },
    limitations: {
      en: [
        'Cannot personalize based on payment history',
        'Generic template, may need customization',
        'Review before sending'
      ],
      hi: [
        'भुगतान इतिहास के आधार पर वैयक्तिकृत नहीं कर सकता',
        'सामान्य टेम्पलेट, अनुकूलन की आवश्यकता हो सकती है',
        'भेजने से पहले समीक्षा करें'
      ]
    },
    useCases: {
      en: ['Monthly reminders', 'Overdue notices', 'Final warnings', 'Bulk messaging'],
      hi: ['मासिक रिमाइंडर', 'अतिदेय सूचनाएं', 'अंतिम चेतावनी', 'बल्क मैसेजिंग']
    }
  },
  'life-skills': {
    name: { en: 'Life Skills Content', hi: 'जीवन कौशल सामग्री' },
    icon: '🌟',
    category: 'all',
    shortDesc: { 
      en: 'Educational content on discipline, safety & habits', 
      hi: 'अनुशासन, सुरक्षा और आदतों पर शैक्षिक सामग्री' 
    },
    fullDesc: {
      en: 'Generates age-appropriate educational content on important life skills including discipline, daily routine, digital safety, manners, and health habits.',
      hi: 'अनुशासन, दैनिक दिनचर्या, डिजिटल सुरक्षा, शिष्टाचार और स्वास्थ्य आदतों सहित महत्वपूर्ण जीवन कौशल पर उम्र-उपयुक्त शैक्षिक सामग्री तैयार करता है।'
    },
    howItWorks: {
      en: [
        'Select a life skill topic',
        'Choose target audience (student/parent)',
        'AI generates educational content',
        'Share via WhatsApp or in class'
      ],
      hi: [
        'एक जीवन कौशल विषय चुनें',
        'लक्षित दर्शक चुनें (छात्र/माता-पिता)',
        'AI शैक्षिक सामग्री तैयार करता है',
        'WhatsApp या कक्षा में साझा करें'
      ]
    },
    benefits: {
      en: [
        'Holistic student development',
        'Ready-to-share content',
        'Age-appropriate messaging',
        'Supports character building'
      ],
      hi: [
        'समग्र छात्र विकास',
        'साझा करने के लिए तैयार सामग्री',
        'उम्र-उपयुक्त संदेश',
        'चरित्र निर्माण में सहायक'
      ]
    },
    limitations: {
      en: [
        'General content, not personalized',
        'May need cultural adaptation',
        'Supplementary to formal education'
      ],
      hi: [
        'सामान्य सामग्री, वैयक्तिकृत नहीं',
        'सांस्कृतिक अनुकूलन की आवश्यकता हो सकती है',
        'औपचारिक शिक्षा के पूरक'
      ]
    },
    useCases: {
      en: ['Morning assembly', 'Parent workshops', 'Student counseling', 'Holiday messages'],
      hi: ['प्रातःकालीन सभा', 'अभिभावक कार्यशालाएं', 'छात्र परामर्श', 'छुट्टी संदेश']
    }
  },
  'complaint-draft': {
    name: { en: 'Complaint Reply Drafter', hi: 'शिकायत उत्तर ड्राफ्टर' },
    icon: '📨',
    category: 'admin',
    shortDesc: { en: 'Draft professional complaint responses', hi: 'पेशेवर शिकायत प्रतिक्रिया बनाएं' },
    fullDesc: { en: 'Creates professional, empathetic responses to parent complaints while maintaining school\'s reputation.', hi: 'स्कूल की प्रतिष्ठा बनाए रखते हुए माता-पिता की शिकायतों का पेशेवर, सहानुभूतिपूर्ण जवाब बनाता है।' },
    howItWorks: { en: ['Enter complaint details', 'Select category', 'AI drafts response', 'Review and send'], hi: ['शिकायत विवरण दर्ज करें', 'श्रेणी चुनें', 'AI प्रतिक्रिया तैयार करता है', 'समीक्षा करें और भेजें'] },
    benefits: { en: ['Professional tone', 'Time saving', 'Consistent responses'], hi: ['पेशेवर टोन', 'समय की बचत', 'सुसंगत प्रतिक्रियाएं'] },
    limitations: { en: ['Cannot assess specific situations', 'Needs human review'], hi: ['विशिष्ट स्थितियों का आकलन नहीं कर सकता', 'मानव समीक्षा आवश्यक'] },
    useCases: { en: ['Academic complaints', 'Fee disputes', 'Safety concerns'], hi: ['शैक्षणिक शिकायतें', 'शुल्क विवाद', 'सुरक्षा चिंताएं'] }
  },
  'compliance-checklist': {
    name: { en: 'Compliance Checklist', hi: 'अनुपालन चेकलिस्ट' },
    icon: '✅',
    category: 'admin',
    shortDesc: { en: 'Government compliance task checklists', hi: 'सरकारी अनुपालन कार्य चेकलिस्ट' },
    fullDesc: { en: 'Generates comprehensive checklists for UDISE+, scholarships, inspections, and annual reports.', hi: 'UDISE+, छात्रवृत्ति, निरीक्षण और वार्षिक रिपोर्ट के लिए व्यापक चेकलिस्ट तैयार करता है।' },
    howItWorks: { en: ['Select task type', 'AI generates checklist', 'Follow step-by-step'], hi: ['कार्य प्रकार चुनें', 'AI चेकलिस्ट तैयार करता है', 'चरण-दर-चरण पालन करें'] },
    benefits: { en: ['Ensures nothing is missed', 'Government portal ready', 'Time saving'], hi: ['कुछ भी छूटने से बचाता है', 'सरकारी पोर्टल तैयार', 'समय की बचत'] },
    limitations: { en: ['Portal procedures may change', 'Generic steps'], hi: ['पोर्टल प्रक्रियाएं बदल सकती हैं', 'सामान्य चरण'] },
    useCases: { en: ['UDISE+ entry', 'Scholarship processing', 'Inspection prep'], hi: ['UDISE+ एंट्री', 'छात्रवृत्ति प्रोसेसिंग', 'निरीक्षण तैयारी'] }
  },
  'chapter-explanation': {
    name: { en: 'Chapter Explainer', hi: 'अध्याय व्याख्याता' },
    icon: '📖',
    category: 'student',
    shortDesc: { en: 'Get chapter explanations and next steps', hi: 'अध्याय व्याख्या और अगले कदम पाएं' },
    fullDesc: { en: 'Explains chapters in simple language with key concepts, common doubts, and suggested next steps.', hi: 'प्रमुख अवधारणाओं, सामान्य संदेहों और सुझाए गए अगले कदमों के साथ सरल भाषा में अध्यायों की व्याख्या करता है।' },
    howItWorks: { en: ['Enter chapter details', 'Add specific doubts', 'Get explanation'], hi: ['अध्याय विवरण दर्ज करें', 'विशिष्ट संदेह जोड़ें', 'व्याख्या प्राप्त करें'] },
    benefits: { en: ['Simple explanations', 'Addresses doubts', 'Clear next steps'], hi: ['सरल व्याख्याएं', 'संदेहों का समाधान', 'स्पष्ट अगले कदम'] },
    limitations: { en: ['Cannot replace teacher', 'Based on general curriculum'], hi: ['शिक्षक की जगह नहीं ले सकता', 'सामान्य पाठ्यक्रम पर आधारित'] },
    useCases: { en: ['Self-study', 'Revision', 'Doubt clearing'], hi: ['स्व-अध्ययन', 'पुनरीक्षण', 'संदेह निवारण'] }
  },
  'micro-task': {
    name: { en: 'Micro Task Suggester', hi: 'माइक्रो टास्क सजेस्टर' },
    icon: '⚡',
    category: 'student',
    shortDesc: { en: 'Quick 10-minute study tasks', hi: '10 मिनट के त्वरित अध्ययन कार्य' },
    fullDesc: { en: 'Suggests quick, achievable study tasks that can be completed in short time periods.', hi: 'त्वरित, प्राप्त करने योग्य अध्ययन कार्य सुझाता है जो कम समय में पूरे किए जा सकते हैं।' },
    howItWorks: { en: ['Set available time', 'Choose subject', 'Get micro tasks'], hi: ['उपलब्ध समय सेट करें', 'विषय चुनें', 'माइक्रो टास्क पाएं'] },
    benefits: { en: ['Productive short breaks', 'Achievable goals', 'Builds study habit'], hi: ['उत्पादक छोटे ब्रेक', 'प्राप्त करने योग्य लक्ष्य', 'अध्ययन आदत बनाता है'] },
    limitations: { en: ['Short tasks only', 'Not for deep learning'], hi: ['केवल छोटे कार्य', 'गहन शिक्षा के लिए नहीं'] },
    useCases: { en: ['Study breaks', 'Bus rides', 'Between classes'], hi: ['अध्ययन ब्रेक', 'बस यात्रा', 'कक्षाओं के बीच'] }
  },
  'homework-revision': {
    name: { en: 'Homework Assistant', hi: 'होमवर्क असिस्टेंट' },
    icon: '📚',
    category: 'student',
    shortDesc: { en: 'Daily homework and revision help', hi: 'दैनिक होमवर्क और रिवीजन मदद' },
    fullDesc: { en: 'Provides homework suggestions and revision content for daily study routines.', hi: 'दैनिक अध्ययन दिनचर्या के लिए होमवर्क सुझाव और रिवीजन सामग्री प्रदान करता है।' },
    howItWorks: { en: ['Select class and subject', 'Get homework ideas', 'Follow revision tips'], hi: ['कक्षा और विषय चुनें', 'होमवर्क आइडिया पाएं', 'रिवीजन टिप्स फॉलो करें'] },
    benefits: { en: ['Structured revision', 'Practice questions', 'Topic coverage'], hi: ['संरचित रिवीजन', 'अभ्यास प्रश्न', 'विषय कवरेज'] },
    limitations: { en: ['General suggestions', 'Not school-specific homework'], hi: ['सामान्य सुझाव', 'स्कूल-विशिष्ट होमवर्क नहीं'] },
    useCases: { en: ['Daily revision', 'Exam prep', 'Holiday homework'], hi: ['दैनिक रिवीजन', 'परीक्षा तैयारी', 'छुट्टी होमवर्क'] }
  },
  'format-adapter': {
    name: { en: 'Format Adapter', hi: 'फॉर्मेट एडॉप्टर' },
    icon: '🔄',
    category: 'all',
    shortDesc: { en: 'Convert content between formats', hi: 'फॉर्मेट के बीच सामग्री कन्वर्ट करें' },
    fullDesc: { en: 'Converts content between detailed, WhatsApp-short, bullet points, and formal formats.', hi: 'विस्तृत, WhatsApp-शॉर्ट, बुलेट पॉइंट्स और औपचारिक फॉर्मेट के बीच सामग्री कन्वर्ट करता है।' },
    howItWorks: { en: ['Paste content', 'Select target format', 'Get converted version'], hi: ['सामग्री पेस्ट करें', 'लक्ष्य फॉर्मेट चुनें', 'कन्वर्टेड वर्जन पाएं'] },
    benefits: { en: ['Multi-channel ready', 'Quick conversion', 'Maintains key info'], hi: ['मल्टी-चैनल तैयार', 'त्वरित रूपांतरण', 'मुख्य जानकारी बनाए रखता है'] },
    limitations: { en: ['May lose some details in short formats'], hi: ['शॉर्ट फॉर्मेट में कुछ विवरण खो सकते हैं'] },
    useCases: { en: ['WhatsApp messages', 'Email to SMS', 'Formal to informal'], hi: ['WhatsApp संदेश', 'ईमेल से SMS', 'औपचारिक से अनौपचारिक'] }
  },
  'complaint-guidance': {
    name: { en: 'Complaint Handling Guidance', hi: 'शिकायत हैंडलिंग मार्गदर्शन' },
    icon: '🔍',
    category: 'admin',
    shortDesc: { en: 'Get guidance on handling complaints', hi: 'शिकायतों को संभालने पर मार्गदर्शन पाएं' },
    fullDesc: { en: 'Provides step-by-step guidance on how to handle different types of complaints professionally.', hi: 'विभिन्न प्रकार की शिकायतों को पेशेवर तरीके से संभालने पर चरण-दर-चरण मार्गदर्शन प्रदान करता है।' },
    howItWorks: { en: ['Enter complaint', 'Get severity assessment', 'Follow action steps'], hi: ['शिकायत दर्ज करें', 'गंभीरता आकलन पाएं', 'कार्रवाई चरणों का पालन करें'] },
    benefits: { en: ['Professional approach', 'Clear action items', 'Timeline guidance'], hi: ['पेशेवर दृष्टिकोण', 'स्पष्ट कार्रवाई', 'समयरेखा मार्गदर्शन'] },
    limitations: { en: ['General guidance only', 'Legal matters need expert'], hi: ['केवल सामान्य मार्गदर्शन', 'कानूनी मामलों में विशेषज्ञ की जरूरत'] },
    useCases: { en: ['New complaints', 'Escalated issues', 'Training staff'], hi: ['नई शिकायतें', 'एस्केलेटेड मुद्दे', 'स्टाफ ट्रेनिंग'] }
  },
  'content-improver': {
    name: { en: 'Content Improver', hi: 'सामग्री सुधारक' },
    icon: '✨',
    category: 'teacher',
    shortDesc: { en: 'Improve homework and notices', hi: 'होमवर्क और नोटिस में सुधार करें' },
    fullDesc: { en: 'Enhances the clarity and quality of teacher-written content like homework and notices.', hi: 'होमवर्क और नोटिस जैसी शिक्षक-लिखित सामग्री की स्पष्टता और गुणवत्ता बढ़ाता है।' },
    howItWorks: { en: ['Paste original content', 'Select content type', 'Get improved version'], hi: ['मूल सामग्री पेस्ट करें', 'सामग्री प्रकार चुनें', 'सुधारित संस्करण पाएं'] },
    benefits: { en: ['Better clarity', 'Professional quality', 'Time saving'], hi: ['बेहतर स्पष्टता', 'पेशेवर गुणवत्ता', 'समय की बचत'] },
    limitations: { en: ['Style may differ from personal preference'], hi: ['शैली व्यक्तिगत पसंद से भिन्न हो सकती है'] },
    useCases: { en: ['Homework instructions', 'Class notices', 'Parent messages'], hi: ['होमवर्क निर्देश', 'कक्षा नोटिस', 'अभिभावक संदेश'] }
  },
  'communication-template': {
    name: { en: 'Communication Templates', hi: 'संचार टेम्पलेट्स' },
    icon: '📱',
    category: 'admin',
    shortDesc: { en: 'Multiple message templates', hi: 'कई संदेश टेम्पलेट्स' },
    fullDesc: { en: 'Generates multiple versions of the same message in formal, friendly, and WhatsApp-short formats.', hi: 'औपचारिक, मैत्रीपूर्ण और WhatsApp-शॉर्ट फॉर्मेट में एक ही संदेश के कई संस्करण तैयार करता है।' },
    howItWorks: { en: ['Enter message purpose', 'Add key points', 'Get multiple templates'], hi: ['संदेश का उद्देश्य दर्ज करें', 'मुख्य बिंदु जोड़ें', 'कई टेम्पलेट पाएं'] },
    benefits: { en: ['Multiple options', 'Audience appropriate', 'Ready to send'], hi: ['कई विकल्प', 'दर्शकों के अनुकूल', 'भेजने के लिए तैयार'] },
    limitations: { en: ['May need personalization'], hi: ['वैयक्तिकरण की आवश्यकता हो सकती है'] },
    useCases: { en: ['Event announcements', 'Holiday notices', 'Reminders'], hi: ['कार्यक्रम घोषणाएं', 'छुट्टी सूचनाएं', 'रिमाइंडर'] }
  },
  'student-support': {
    name: { en: 'Student Support Identifier', hi: 'छात्र सहायता पहचानकर्ता' },
    icon: '🎯',
    category: 'teacher',
    shortDesc: { en: 'Identify students needing support', hi: 'सहायता की जरूरत वाले छात्रों की पहचान' },
    fullDesc: { en: 'Analyzes student data to identify those who may need additional academic or emotional support.', hi: 'छात्र डेटा का विश्लेषण करके उन लोगों की पहचान करता है जिन्हें अतिरिक्त शैक्षणिक या भावनात्मक सहायता की आवश्यकता हो सकती है।' },
    howItWorks: { en: ['Enter student ID', 'AI analyzes data', 'Get recommendations'], hi: ['छात्र ID दर्ज करें', 'AI डेटा का विश्लेषण करता है', 'सिफारिशें पाएं'] },
    benefits: { en: ['Early intervention', 'Data-driven insights', 'Prioritized support'], hi: ['शीघ्र हस्तक्षेप', 'डेटा-संचालित अंतर्दृष्टि', 'प्राथमिकता सहायता'] },
    limitations: { en: ['Based on available data only', 'Needs human judgment'], hi: ['केवल उपलब्ध डेटा पर आधारित', 'मानव निर्णय आवश्यक'] },
    useCases: { en: ['Attendance issues', 'Performance tracking', 'Counseling referrals'], hi: ['उपस्थिति मुद्दे', 'प्रदर्शन ट्रैकिंग', 'काउंसलिंग रेफरल'] }
  },
  'workflow-reminder': {
    name: { en: 'Workflow Reminders', hi: 'वर्कफ़्लो रिमाइंडर' },
    icon: '⏰',
    category: 'admin',
    shortDesc: { en: 'Auto-generate workflow reminders', hi: 'स्वचालित वर्कफ़्लो रिमाइंडर' },
    fullDesc: { en: 'Generates automated reminders for various school workflows in multiple formats (WhatsApp, SMS, Email).', hi: 'विभिन्न स्कूल वर्कफ़्लो के लिए कई फॉर्मेट (WhatsApp, SMS, ईमेल) में स्वचालित रिमाइंडर तैयार करता है।' },
    howItWorks: { en: ['Select reminder type', 'Add details', 'Get multi-format messages'], hi: ['रिमाइंडर प्रकार चुनें', 'विवरण जोड़ें', 'मल्टी-फॉर्मेट संदेश पाएं'] },
    benefits: { en: ['Consistent reminders', 'Multiple formats', 'Time saving'], hi: ['सुसंगत रिमाइंडर', 'कई फॉर्मेट', 'समय की बचत'] },
    limitations: { en: ['Generic templates', 'Needs scheduling separately'], hi: ['सामान्य टेम्पलेट', 'अलग से शेड्यूलिंग आवश्यक'] },
    useCases: { en: ['Fee due', 'Event reminders', 'Compliance deadlines'], hi: ['शुल्क देय', 'कार्यक्रम रिमाइंडर', 'अनुपालन समय सीमा'] }
  },
  'parent-portal-simplify': {
    name: { en: 'Parent Portal Simplifier', hi: 'पेरेंट पोर्टल सरलकर्ता' },
    icon: '👨‍👩‍👧',
    category: 'parent',
    shortDesc: { en: 'Simplify notices for parent portal', hi: 'पेरेंट पोर्टल के लिए नोटिस सरल करें' },
    fullDesc: { en: 'Creates multiple simplified versions of notices specifically for the parent portal audience.', hi: 'विशेष रूप से पेरेंट पोर्टल दर्शकों के लिए नोटिस के कई सरलीकृत संस्करण बनाता है।' },
    howItWorks: { en: ['Paste notice', 'Get 3 versions', 'Choose best fit'], hi: ['नोटिस पेस्ट करें', '3 संस्करण पाएं', 'सर्वश्रेष्ठ चुनें'] },
    benefits: { en: ['Multiple options', 'Parent-friendly language', 'Key points highlighted'], hi: ['कई विकल्प', 'अभिभावक-अनुकूल भाषा', 'मुख्य बिंदु हाइलाइट'] },
    limitations: { en: ['May lose technical details'], hi: ['तकनीकी विवरण खो सकते हैं'] },
    useCases: { en: ['Policy notices', 'Event details', 'Important updates'], hi: ['नीति सूचनाएं', 'कार्यक्रम विवरण', 'महत्वपूर्ण अपडेट'] }
  },
  // ============ NEW AI FEATURES - Adding missing features ============
  'class-weak-topics': {
    name: { en: 'Class Weak Topics', hi: 'क्लास Weak Topics' },
    icon: '📉',
    category: 'teacher',
    shortDesc: { en: 'Analyze common weak topics from class doubt history', hi: 'क्लास के doubt history से common weak topics analyze करें' },
    fullDesc: { en: 'This AI feature analyzes the entire class doubt history to identify common weak topics and areas where students need more attention. Perfect for teachers to plan targeted revision sessions.', hi: 'यह AI सुविधा पूरी class के doubt history को analyze करके common weak topics और उन areas को पहचानती है जहां students को ज्यादा ध्यान देने की जरूरत है। Teachers के लिए targeted revision sessions plan करने में मददगार।' },
    howItWorks: { en: ['Enter class name', 'Optionally add subject filter', 'AI analyzes all doubts', 'Shows weak topics with frequency'], hi: ['क्लास का नाम दर्ज करें', 'वैकल्पिक: विषय filter करें', 'AI सभी doubts analyze करता है', 'Weak topics frequency के साथ दिखाता है'] },
    benefits: { en: ['Data-driven insights', 'Helps prioritize revision', 'Identifies struggling areas', 'Improves teaching effectiveness'], hi: ['Data-driven insights', 'Revision prioritize करने में मदद', 'Struggling areas identify करता है', 'Teaching effectiveness बढ़ाता है'] },
    limitations: { en: ['Needs sufficient doubt history', 'Analysis based on recorded doubts only'], hi: ['पर्याप्त doubt history जरूरी है', 'केवल recorded doubts पर based है'] },
    useCases: { en: ['Exam preparation', 'Revision planning', 'Identifying weak students', 'PTM discussions'], hi: ['Exam preparation', 'Revision planning', 'Weak students identify करना', 'PTM discussions'] }
  },
  'question-paper-gen': {
    name: { en: 'Question Paper Generator', hi: 'प्रश्न पत्र जनरेटर' },
    icon: '📄',
    category: 'teacher',
    shortDesc: { en: 'Generate balanced question papers in CBSE pattern', hi: 'CBSE pattern में balanced question paper generate करें' },
    fullDesc: { en: 'Automatically generates balanced question papers with MCQ, Short Answer, and Long Answer questions following CBSE guidelines. Includes difficulty distribution and topic coverage.', hi: 'CBSE guidelines के अनुसार MCQ, Short Answer, और Long Answer questions के साथ balanced question papers automatically generate करता है। Difficulty distribution और topic coverage शामिल है।' },
    howItWorks: { en: ['Enter subject and class', 'Set total marks and duration', 'Add topics to cover', 'AI generates balanced paper'], hi: ['विषय और कक्षा दर्ज करें', 'कुल अंक और समय set करें', 'Cover करने के लिए topics जोड़ें', 'AI balanced paper generate करता है'] },
    benefits: { en: ['CBSE pattern compliant', 'Balanced difficulty distribution', 'Saves hours of work', 'Consistent quality'], hi: ['CBSE pattern अनुरूप', 'Balanced difficulty distribution', 'घंटों का काम बचाता है', 'Consistent quality'] },
    limitations: { en: ['Review required before use', 'May need manual adjustments', 'General syllabus based'], hi: ['Use से पहले review जरूरी', 'Manual adjustments की जरूरत हो सकती है', 'General syllabus पर based'] },
    useCases: { en: ['Unit tests', 'Term exams', 'Practice papers', 'Mock tests'], hi: ['Unit tests', 'Term exams', 'Practice papers', 'Mock tests'] }
  },
  'report-card-gen': {
    name: { en: 'AI Report Card', hi: 'रिपोर्ट कार्ड AI' },
    icon: '🎓',
    category: 'admin',
    shortDesc: { en: 'Auto-generate report cards with AI remarks', hi: 'AI remarks के साथ auto report card generate करें' },
    fullDesc: { en: 'Generates comprehensive report cards with personalized teacher remarks, strength analysis, and improvement suggestions based on student performance data.', hi: 'Student performance data के आधार पर personalized teacher remarks, strength analysis, और improvement suggestions के साथ comprehensive report cards generate करता है।' },
    howItWorks: { en: ['Enter student ID', 'Select term type', 'AI analyzes performance', 'Generates report with remarks'], hi: ['Student ID दर्ज करें', 'Term type चुनें', 'AI performance analyze करता है', 'Remarks के साथ report generate करता है'] },
    benefits: { en: ['Personalized remarks', 'Time saving', 'Consistent quality', 'Actionable suggestions'], hi: ['Personalized remarks', 'समय की बचत', 'Consistent quality', 'Actionable suggestions'] },
    limitations: { en: ['Based on available data only', 'Teacher review recommended'], hi: ['केवल available data पर based', 'Teacher review recommended'] },
    useCases: { en: ['Term-end reports', 'PTM preparation', 'Progress tracking', 'Parent communication'], hi: ['Term-end reports', 'PTM preparation', 'Progress tracking', 'Parent communication'] }
  },
  'at-risk-alerts': {
    name: { en: 'At-Risk Alerts', hi: 'At-Risk अलर्ट्स' },
    icon: '⚠️',
    category: 'teacher',
    shortDesc: { en: 'Early identify struggling students', hi: 'पिछड़ते students की early पहचान करें' },
    fullDesc: { en: 'AI-powered early warning system that identifies students at risk based on attendance patterns, marks trends, and other indicators. Helps teachers intervene before it\'s too late.', hi: 'AI-powered early warning system जो attendance patterns, marks trends, और अन्य indicators के आधार पर at-risk students identify करता है। Teachers को समय पर intervention में मदद करता है।' },
    howItWorks: { en: ['Set attendance threshold', 'Set marks threshold', 'AI scans student data', 'Flags at-risk students'], hi: ['Attendance threshold set करें', 'Marks threshold set करें', 'AI student data scan करता है', 'At-risk students flag करता है'] },
    benefits: { en: ['Early intervention', 'Data-driven alerts', 'Prevents dropouts', 'Prioritized attention'], hi: ['Early intervention', 'Data-driven alerts', 'Dropouts रोकता है', 'Prioritized attention'] },
    limitations: { en: ['Needs sufficient data', 'May have false positives'], hi: ['पर्याप्त data जरूरी है', 'False positives हो सकते हैं'] },
    useCases: { en: ['Monthly review', 'Counseling referrals', 'Parent meetings', 'Support planning'], hi: ['Monthly review', 'Counseling referrals', 'Parent meetings', 'Support planning'] }
  },
  'child-progress': {
    name: { en: 'Child Progress', hi: 'बच्चे की प्रगति' },
    icon: '📈',
    category: 'parent',
    shortDesc: { en: 'Complete child progress dashboard', hi: 'बच्चे की complete progress dashboard' },
    fullDesc: { en: 'Comprehensive progress tracking for parents showing academics, attendance, behavior, and learning activities in an easy-to-understand format.', hi: 'Parents के लिए comprehensive progress tracking जो academics, attendance, behavior, और learning activities को आसान format में दिखाता है।' },
    howItWorks: { en: ['Enter student ID', 'Select time period', 'View comprehensive report', 'Track trends over time'], hi: ['Student ID दर्ज करें', 'Time period चुनें', 'Comprehensive report देखें', 'समय के साथ trends track करें'] },
    benefits: { en: ['Complete visibility', 'Easy to understand', 'Regular updates', 'Actionable insights'], hi: ['Complete visibility', 'समझने में आसान', 'Regular updates', 'Actionable insights'] },
    limitations: { en: ['Depends on school data entry', 'May not capture all activities'], hi: ['School data entry पर निर्भर', 'सभी activities capture नहीं हो सकतीं'] },
    useCases: { en: ['Weekly check-ins', 'PTM preparation', 'Goal setting', 'Motivation'], hi: ['Weekly check-ins', 'PTM preparation', 'Goal setting', 'Motivation'] }
  },
  'daily-summary': {
    name: { en: 'Daily Summary', hi: 'दैनिक सारांश' },
    icon: '📋',
    category: 'parent',
    shortDesc: { en: 'What child learned today - daily AI summary', hi: 'आज school में बच्चे ने क्या सीखा - daily AI summary' },
    fullDesc: { en: 'Generates a daily summary of what the child learned at school, including topics covered, activities participated in, and homework assigned. Perfect for busy parents to stay connected.', hi: 'बच्चे ने school में आज क्या सीखा इसका daily summary generate करता है, जिसमें topics covered, activities, और homework शामिल है। Busy parents के लिए connected रहने का perfect तरीका।' },
    howItWorks: { en: ['Enter student ID', 'Select date', 'Teacher adds daily topics', 'AI generates parent-friendly summary'], hi: ['Student ID दर्ज करें', 'Date चुनें', 'Teacher daily topics जोड़ता है', 'AI parent-friendly summary generate करता है'] },
    benefits: { en: ['Daily connection', 'WhatsApp-ready format', 'Saves teacher time', 'Engaging for parents'], hi: ['Daily connection', 'WhatsApp-ready format', 'Teacher का समय बचाता है', 'Parents के लिए engaging'] },
    limitations: { en: ['Needs teacher input', 'Generic if no specific data'], hi: ['Teacher input जरूरी है', 'Specific data न हो तो generic'] },
    useCases: { en: ['Daily parent updates', 'WhatsApp broadcasts', 'Parent engagement', 'Homework awareness'], hi: ['Daily parent updates', 'WhatsApp broadcasts', 'Parent engagement', 'Homework awareness'] }
  },
  'attendance-alerts': {
    name: { en: 'Attendance Alerts', hi: 'उपस्थिति अलर्ट' },
    icon: '🔔',
    category: 'parent',
    shortDesc: { en: 'Instant alerts for attendance issues', hi: 'Attendance issues के लिए instant alerts' },
    fullDesc: { en: 'Monitors child\'s attendance patterns and alerts parents about concerning trends like consecutive absences, frequent late arrivals, or declining attendance percentage.', hi: 'बच्चे के attendance patterns monitor करता है और parents को concerning trends जैसे consecutive absences, frequent late arrivals, या declining attendance के बारे में alert करता है।' },
    howItWorks: { en: ['Enter student ID', 'Set alert threshold', 'AI monitors patterns', 'Sends alerts when needed'], hi: ['Student ID दर्ज करें', 'Alert threshold set करें', 'AI patterns monitor करता है', 'जरूरत पर alerts भेजता है'] },
    benefits: { en: ['Early awareness', 'Prevents chronic absence', 'Pattern detection', 'Timely intervention'], hi: ['Early awareness', 'Chronic absence रोकता है', 'Pattern detection', 'Timely intervention'] },
    limitations: { en: ['Needs regular attendance entry', 'May not capture all reasons'], hi: ['Regular attendance entry जरूरी है', 'सभी reasons capture नहीं हो सकते'] },
    useCases: { en: ['Absence monitoring', 'Pattern alerts', 'Parent-teacher communication', 'Intervention planning'], hi: ['Absence monitoring', 'Pattern alerts', 'Parent-teacher communication', 'Intervention planning'] }
  },
  'practice-tests': {
    name: { en: 'Practice Tests', hi: 'प्रैक्टिस टेस्ट' },
    icon: '📝',
    category: 'student',
    shortDesc: { en: 'Unlimited practice tests with instant results', hi: 'Instant results के साथ unlimited practice tests' },
    fullDesc: { en: 'Take unlimited practice tests on any subject and topic. Get instant results with detailed explanations. Perfect for exam preparation and self-assessment.', hi: 'किसी भी विषय और topic पर unlimited practice tests लें। Detailed explanations के साथ instant results पाएं। Exam preparation और self-assessment के लिए perfect।' },
    howItWorks: { en: ['Select subject and topic', 'Set number of questions', 'Take the test', 'Get instant results and explanations'], hi: ['विषय और topic चुनें', 'प्रश्नों की संख्या set करें', 'Test दें', 'Instant results और explanations पाएं'] },
    benefits: { en: ['Unlimited practice', 'Instant feedback', 'Detailed explanations', 'Track progress'], hi: ['Unlimited practice', 'Instant feedback', 'Detailed explanations', 'Progress track करें'] },
    limitations: { en: ['AI-generated questions', 'May not match exact board pattern'], hi: ['AI-generated questions', 'Exact board pattern से match न हो सकता'] },
    useCases: { en: ['Daily practice', 'Exam preparation', 'Weak area focus', 'Competition prep'], hi: ['Daily practice', 'Exam preparation', 'Weak area focus', 'Competition prep'] }
  },
  'gamified-learning': {
    name: { en: 'Gamified Learning', hi: 'गेमिफाइड लर्निंग' },
    icon: '🏆',
    category: 'student',
    shortDesc: { en: 'Earn badges, build streaks, top the leaderboard', hi: 'Badges कमाओ, Streaks बनाओ, Leaderboard में आओ' },
    fullDesc: { en: 'Make learning fun with gamification! Earn badges for achievements, maintain daily streaks, compete on leaderboards, and unlock rewards. Perfect for keeping students motivated.', hi: 'Gamification के साथ पढ़ाई को मजेदार बनाएं! Achievements के लिए badges कमाएं, daily streaks बनाए रखें, leaderboards पर compete करें, और rewards unlock करें। Students को motivated रखने के लिए perfect।' },
    howItWorks: { en: ['Complete learning tasks', 'Earn points and badges', 'Maintain daily streak', 'Climb the leaderboard'], hi: ['Learning tasks पूरे करें', 'Points और badges कमाएं', 'Daily streak बनाए रखें', 'Leaderboard में आगे बढ़ें'] },
    benefits: { en: ['Increased motivation', 'Regular engagement', 'Healthy competition', 'Fun learning'], hi: ['Increased motivation', 'Regular engagement', 'Healthy competition', 'Fun learning'] },
    limitations: { en: ['Requires regular participation', 'May create over-competition'], hi: ['Regular participation जरूरी है', 'Over-competition हो सकता है'] },
    useCases: { en: ['Daily motivation', 'Class competitions', 'Holiday challenges', 'Reward programs'], hi: ['Daily motivation', 'Class competitions', 'Holiday challenges', 'Reward programs'] }
  },
  'revision-scheduler': {
    name: { en: 'Revision Scheduler', hi: 'रिवीज़न शेड्यूलर' },
    icon: '🗓️',
    category: 'student',
    shortDesc: { en: 'Smart revision schedule with spaced repetition', hi: 'Spaced repetition के साथ smart revision schedule' },
    fullDesc: { en: 'Creates an optimized revision schedule using spaced repetition technique. Scientifically proven to improve retention and recall. Personalized based on exam date and available time.', hi: 'Spaced repetition technique का उपयोग करके optimized revision schedule बनाता है। Retention और recall improve करने के लिए scientifically proven। Exam date और available time के आधार पर personalized।' },
    howItWorks: { en: ['Enter subject and topics', 'Set exam date', 'Set daily study time', 'Get optimized schedule'], hi: ['विषय और topics दर्ज करें', 'Exam date set करें', 'Daily study time set करें', 'Optimized schedule पाएं'] },
    benefits: { en: ['Better retention', 'Prevents cramming', 'Scientific approach', 'Personalized plan'], hi: ['Better retention', 'Cramming रोकता है', 'Scientific approach', 'Personalized plan'] },
    limitations: { en: ['Requires commitment', 'Needs sufficient time before exam'], hi: ['Commitment जरूरी है', 'Exam से पहले पर्याप्त समय चाहिए'] },
    useCases: { en: ['Board exam prep', 'Competition exams', 'Regular revision', 'Long-term memory'], hi: ['Board exam prep', 'Competition exams', 'Regular revision', 'Long-term memory'] }
  },
  'weekly-challenge': {
    name: { en: 'Weekly Challenge', hi: 'साप्ताहिक चैलेंज' },
    icon: '🎯',
    category: 'student',
    shortDesc: { en: 'New quiz challenge every week - compete and win!', hi: 'हर हफ्ते नया quiz challenge - compete करो और जीतो!' },
    fullDesc: { en: 'Participate in weekly quiz challenges! Compete with classmates, earn points, and see your name on the leaderboard. New challenge every Monday, perfect for keeping learning exciting.', hi: 'Weekly quiz challenges में participate करो! Classmates से compete करो, points कमाओ, और leaderboard पर अपना नाम देखो। हर Monday नया challenge, learning को exciting रखने के लिए perfect।' },
    howItWorks: { en: ['Select class and section', 'View current week challenge', 'Take the quiz', 'Check leaderboard rankings'], hi: ['कक्षा और section चुनें', 'इस हफ्ते का challenge देखें', 'Quiz दें', 'Leaderboard rankings check करें'] },
    benefits: { en: ['Weekly engagement', 'Healthy competition', 'Recognition for top scorers', 'Consistent practice'], hi: ['Weekly engagement', 'Healthy competition', 'Top scorers को recognition', 'Consistent practice'] },
    limitations: { en: ['Weekly deadline', 'Requires class and section selection'], hi: ['Weekly deadline', 'Class और section selection जरूरी है'] },
    useCases: { en: ['Class competitions', 'Weekly motivation', 'Peer learning', 'Assessment'], hi: ['Class competitions', 'Weekly motivation', 'Peer learning', 'Assessment'] }
  },
  // ============ PLATFORM MODULE FEATURES ============
  'timetable-generator': {
    name: { en: 'Smart Timetable Generator', hi: 'स्मार्ट टाइमटेबल जनरेटर' },
    icon: '🕐',
    category: 'admin',
    shortDesc: { en: 'AI-powered automatic timetable creation', hi: 'AI-powered automatic timetable बनाएं' },
    fullDesc: { en: 'Automatically generates optimized class timetables considering teacher availability, subject requirements, and room constraints. Saves hours of manual scheduling.', hi: 'Teacher availability, subject requirements, और room constraints को ध्यान में रखते हुए automatically optimized class timetable generate करता है। Manual scheduling के घंटों की बचत।' },
    howItWorks: { en: ['Set teacher availability', 'Define subject hours per week', 'AI generates optimized timetable', 'Review and publish'], hi: ['Teacher availability set करें', 'Weekly subject hours define करें', 'AI optimized timetable generate करता है', 'Review करें और publish करें'] },
    benefits: { en: ['Saves hours of planning', 'No scheduling conflicts', 'Optimized distribution', 'Easy modifications'], hi: ['Planning के घंटों की बचत', 'कोई scheduling conflict नहीं', 'Optimized distribution', 'आसान modifications'] },
    limitations: { en: ['Needs complete teacher data', 'Manual review recommended'], hi: ['Complete teacher data जरूरी', 'Manual review recommended'] },
    useCases: { en: ['New session planning', 'Mid-term changes', 'Substitute teacher scheduling'], hi: ['New session planning', 'Mid-term changes', 'Substitute teacher scheduling'] }
  },
  'leave-management': {
    name: { en: 'Leave Management System', hi: 'छुट्टी प्रबंधन सिस्टम' },
    icon: '📋',
    category: 'admin',
    shortDesc: { en: 'Digital leave application and approval', hi: 'Digital छुट्टी आवेदन और approval' },
    fullDesc: { en: 'Complete digital leave management for teachers and staff. Apply, approve, and track leaves with automated notifications to parents about teacher absences.', hi: 'Teachers और staff के लिए complete digital leave management। Apply, approve, और track leaves with automated notifications।' },
    howItWorks: { en: ['Teacher applies for leave online', 'Admin gets notification', 'One-click approval/rejection', 'Parents notified automatically'], hi: ['Teacher online leave apply करता है', 'Admin को notification आता है', 'One-click approval/rejection', 'Parents को automatically notify होता है'] },
    benefits: { en: ['Paperless process', 'Instant notifications', 'Leave balance tracking', 'Substitute planning'], hi: ['Paperless process', 'Instant notifications', 'Leave balance tracking', 'Substitute planning'] },
    limitations: { en: ['Requires internet connectivity', 'Initial setup needed'], hi: ['Internet connectivity जरूरी', 'Initial setup जरूरी'] },
    useCases: { en: ['Teacher leave requests', 'Staff attendance', 'Leave reports'], hi: ['Teacher leave requests', 'Staff attendance', 'Leave reports'] }
  },
  'transport-management': {
    name: { en: 'Transport Management', hi: 'ट्रांसपोर्ट प्रबंधन' },
    icon: '🚌',
    category: 'admin',
    shortDesc: { en: 'School bus route and tracking management', hi: 'स्कूल बस route और tracking management' },
    fullDesc: { en: 'Manage school transport with route planning, driver assignment, and student pickup/drop tracking. Parents get real-time notifications about bus status.', hi: 'Route planning, driver assignment, और student pickup/drop tracking के साथ school transport manage करें। Parents को bus status की real-time notifications मिलती हैं।' },
    howItWorks: { en: ['Define bus routes', 'Assign students to routes', 'Track bus in real-time', 'Parents get pickup alerts'], hi: ['Bus routes define करें', 'Students को routes assign करें', 'Real-time bus track करें', 'Parents को pickup alerts मिलें'] },
    benefits: { en: ['Route optimization', 'Safety tracking', 'Parent peace of mind', 'Cost management'], hi: ['Route optimization', 'Safety tracking', 'Parents को शांति', 'Cost management'] },
    limitations: { en: ['GPS device required on buses', 'Internet connectivity needed'], hi: ['Buses पर GPS device जरूरी', 'Internet connectivity जरूरी'] },
    useCases: { en: ['Daily transport', 'Field trips', 'Emergency tracking'], hi: ['Daily transport', 'Field trips', 'Emergency tracking'] }
  },
  'emergency-exit': {
    name: { en: 'Emergency Exit System', hi: 'इमरजेंसी एक्ज़िट सिस्टम' },
    icon: '🚨',
    category: 'admin',
    shortDesc: { en: 'Quick student release with parent verification', hi: 'Parent verification के साथ quick student release' },
    fullDesc: { en: 'Secure emergency student release system with OTP-based parent verification. Ensures only authorized persons can pick up students during emergencies or early dismissals.', hi: 'OTP-based parent verification के साथ secure emergency student release system। सुनिश्चित करता है कि emergencies या early dismissals के दौरान केवल authorized व्यक्ति ही students को pick up कर सकें।' },
    howItWorks: { en: ['Parent requests early pickup', 'School verifies via OTP', 'Student released securely', 'Digital log maintained'], hi: ['Parent early pickup request करता है', 'School OTP से verify करता है', 'Student securely release होता है', 'Digital log maintain होता है'] },
    benefits: { en: ['Child safety first', 'Verified release only', 'Digital audit trail', 'Parent confidence'], hi: ['Child safety first', 'Verified release only', 'Digital audit trail', 'Parent confidence'] },
    limitations: { en: ['Requires mobile number', 'Network dependent'], hi: ['Mobile number जरूरी', 'Network dependent'] },
    useCases: { en: ['Early dismissal', 'Medical emergencies', 'Parent pickup'], hi: ['Early dismissal', 'Medical emergencies', 'Parent pickup'] }
  },
  'online-fee-payment': {
    name: { en: 'Online Fee Payment', hi: 'ऑनलाइन फीस भुगतान' },
    icon: '💳',
    category: 'parent',
    shortDesc: { en: 'Pay school fees online with multiple options', hi: 'Multiple options के साथ online school fees pay करें' },
    fullDesc: { en: 'Complete online fee payment system with UPI, cards, net banking, and EMI options. Parents can view fee breakdown, download receipts, and track payment history.', hi: 'UPI, cards, net banking, और EMI options के साथ complete online fee payment system। Parents fee breakdown देख सकते हैं, receipts download कर सकते हैं, और payment history track कर सकते हैं।' },
    howItWorks: { en: ['View fee breakdown', 'Choose payment method', 'Pay securely online', 'Get instant receipt'], hi: ['Fee breakdown देखें', 'Payment method चुनें', 'Securely online pay करें', 'Instant receipt पाएं'] },
    benefits: { en: ['No cash handling', 'Instant receipts', 'EMI available', 'Payment history'], hi: ['Cash handling नहीं', 'Instant receipts', 'EMI available', 'Payment history'] },
    limitations: { en: ['Internet required', 'Transaction charges may apply'], hi: ['Internet जरूरी', 'Transaction charges लग सकते हैं'] },
    useCases: { en: ['Monthly fees', 'Exam fees', 'Activity fees', 'EMI payments'], hi: ['Monthly fees', 'Exam fees', 'Activity fees', 'EMI payments'] }
  },
  'bulk-import': {
    name: { en: 'Bulk Data Import', hi: 'बल्क डेटा इम्पोर्ट' },
    icon: '📤',
    category: 'admin',
    shortDesc: { en: 'Import students, teachers, fees via Excel', hi: 'Excel से students, teachers, fees import करें' },
    fullDesc: { en: 'Import large amounts of data quickly using Excel files. Supports student enrollment, teacher data, fee structures, and more with validation and error reporting.', hi: 'Excel files का उपयोग करके बड़ी मात्रा में data quickly import करें। Student enrollment, teacher data, fee structures, और अधिक support करता है validation और error reporting के साथ।' },
    howItWorks: { en: ['Download template Excel', 'Fill in data', 'Upload file', 'Review and confirm import'], hi: ['Template Excel download करें', 'Data fill करें', 'File upload करें', 'Review करें और import confirm करें'] },
    benefits: { en: ['Save hours of data entry', 'Error validation', 'Template provided', 'Batch processing'], hi: ['Data entry के घंटों की बचत', 'Error validation', 'Template provided', 'Batch processing'] },
    limitations: { en: ['Excel format required', 'Large files may take time'], hi: ['Excel format जरूरी', 'Large files में समय लग सकता है'] },
    useCases: { en: ['New session enrollment', 'Teacher onboarding', 'Fee setup', 'Data migration'], hi: ['New session enrollment', 'Teacher onboarding', 'Fee setup', 'Data migration'] }
  },
  'whatsapp-broadcast': {
    name: { en: 'WhatsApp Broadcasting', hi: 'WhatsApp ब्रॉडकास्टिंग' },
    icon: '📲',
    category: 'admin',
    shortDesc: { en: 'Send bulk WhatsApp messages to parents', hi: 'Parents को bulk WhatsApp messages भेजें' },
    fullDesc: { en: 'Send important school updates, fee reminders, event notifications, and exam schedules to all parents via WhatsApp. Supports templates, scheduling, and delivery tracking.', hi: 'WhatsApp के माध्यम से सभी parents को important school updates, fee reminders, event notifications, और exam schedules भेजें। Templates, scheduling, और delivery tracking support करता है।' },
    howItWorks: { en: ['Select recipient group', 'Choose or create template', 'Schedule or send now', 'Track delivery status'], hi: ['Recipient group select करें', 'Template choose या create करें', 'Schedule करें या अभी भेजें', 'Delivery status track करें'] },
    benefits: { en: ['98% open rate', 'Instant delivery', 'Template library', 'Delivery tracking'], hi: ['98% open rate', 'Instant delivery', 'Template library', 'Delivery tracking'] },
    limitations: { en: ['WhatsApp Business API required', 'Template approval needed'], hi: ['WhatsApp Business API जरूरी', 'Template approval जरूरी'] },
    useCases: { en: ['Fee reminders', 'Event notifications', 'Exam schedules', 'Holiday announcements'], hi: ['Fee reminders', 'Event notifications', 'Exam schedules', 'Holiday announcements'] }
  },
  'udise-portal': {
    name: { en: 'UDISE+ Portal Integration', hi: 'UDISE+ पोर्टल इंटीग्रेशन' },
    icon: '🏛️',
    category: 'admin',
    shortDesc: { en: 'Auto-fill UDISE+ forms with school data', hi: 'School data से UDISE+ forms auto-fill करें' },
    fullDesc: { en: 'Simplifies UDISE+ compliance by auto-generating data required for government portal submissions. Pre-fills forms with school data, reducing errors and saving time.', hi: 'Government portal submissions के लिए required data auto-generate करके UDISE+ compliance को simplify करता है। School data से forms pre-fill करता है, errors कम करता है और समय बचाता है।' },
    howItWorks: { en: ['Complete school profile', 'AI maps data to UDISE+ fields', 'Review pre-filled forms', 'Submit to government portal'], hi: ['School profile complete करें', 'AI data को UDISE+ fields में map करता है', 'Pre-filled forms review करें', 'Government portal पर submit करें'] },
    benefits: { en: ['Reduces errors', 'Saves hours', 'Compliance ready', 'Data validation'], hi: ['Errors कम करता है', 'घंटों की बचत', 'Compliance ready', 'Data validation'] },
    limitations: { en: ['Government portal may change', 'Internet required'], hi: ['Government portal बदल सकता है', 'Internet जरूरी'] },
    useCases: { en: ['Annual UDISE+ submission', 'Data verification', 'Compliance audit'], hi: ['Annual UDISE+ submission', 'Data verification', 'Compliance audit'] }
  },
  'social-media-manager': {
    name: { en: 'Social Media Manager', hi: 'सोशल मीडिया मैनेजर' },
    icon: '📱',
    category: 'admin',
    shortDesc: { en: 'Manage school social media from one place', hi: 'एक जगह से school social media manage करें' },
    fullDesc: { en: 'Create, schedule, and publish social media posts for your school across platforms. AI generates engaging captions, hashtags, and content ideas tailored for education.', hi: 'अपने school के लिए social media posts create, schedule, और publish करें। AI engaging captions, hashtags, और education के लिए content ideas generate करता है।' },
    howItWorks: { en: ['Create post with AI assistance', 'Add images or use templates', 'Schedule across platforms', 'Track engagement analytics'], hi: ['AI assistance से post create करें', 'Images add करें या templates use करें', 'Platforms पर schedule करें', 'Engagement analytics track करें'] },
    benefits: { en: ['Consistent branding', 'AI content ideas', 'Multi-platform', 'Analytics'], hi: ['Consistent branding', 'AI content ideas', 'Multi-platform', 'Analytics'] },
    limitations: { en: ['Platform API access needed', 'Content review recommended'], hi: ['Platform API access जरूरी', 'Content review recommended'] },
    useCases: { en: ['School events', 'Achievement posts', 'Admission campaigns', 'Festival wishes'], hi: ['School events', 'Achievement posts', 'Admission campaigns', 'Festival wishes'] }
  },
  'school-profile': {
    name: { en: 'School Profile Page', hi: 'स्कूल प्रोफाइल पेज' },
    icon: '🏫',
    category: 'admin',
    shortDesc: { en: 'Beautiful online school profile for parents', hi: 'Parents के लिए beautiful online school profile' },
    fullDesc: { en: 'Create a professional online presence for your school with customizable profile pages. Showcase facilities, achievements, faculty, and more to attract admissions.', hi: 'Customizable profile pages के साथ अपने school के लिए professional online presence create करें। Admissions attract करने के लिए facilities, achievements, faculty, और अधिक showcase करें।' },
    howItWorks: { en: ['Fill school details', 'Upload photos and videos', 'Customize theme colors', 'Share profile link'], hi: ['School details fill करें', 'Photos और videos upload करें', 'Theme colors customize करें', 'Profile link share करें'] },
    benefits: { en: ['Professional presence', 'Admission boost', 'Easy sharing', 'SEO friendly'], hi: ['Professional presence', 'Admission boost', 'Easy sharing', 'SEO friendly'] },
    limitations: { en: ['Content needs regular updates', 'Photos required'], hi: ['Content को regular updates चाहिए', 'Photos जरूरी'] },
    useCases: { en: ['Admission season', 'School marketing', 'Parent information', 'Government inspections'], hi: ['Admission season', 'School marketing', 'Parent information', 'Government inspections'] }
  },
  'analytics-dashboard': {
    name: { en: 'Analytics Dashboard', hi: 'एनालिटिक्स डैशबोर्ड' },
    icon: '📊',
    category: 'admin',
    shortDesc: { en: 'Complete school analytics and insights', hi: 'Complete school analytics और insights' },
    fullDesc: { en: 'Comprehensive analytics dashboard showing student performance trends, attendance patterns, fee collection status, and operational metrics. Make data-driven decisions for your school.', hi: 'Student performance trends, attendance patterns, fee collection status, और operational metrics दिखाने वाला comprehensive analytics dashboard। अपने school के लिए data-driven decisions लें।' },
    howItWorks: { en: ['View real-time metrics', 'Filter by class, subject, period', 'Export reports', 'Set performance alerts'], hi: ['Real-time metrics देखें', 'Class, subject, period से filter करें', 'Reports export करें', 'Performance alerts set करें'] },
    benefits: { en: ['Data-driven decisions', 'Real-time insights', 'Trend analysis', 'Export reports'], hi: ['Data-driven decisions', 'Real-time insights', 'Trend analysis', 'Export reports'] },
    limitations: { en: ['Needs regular data entry', 'Historical data required for trends'], hi: ['Regular data entry जरूरी', 'Trends के लिए historical data जरूरी'] },
    useCases: { en: ['Board meetings', 'Parent presentations', 'Performance reviews', 'Planning'], hi: ['Board meetings', 'Parent presentations', 'Performance reviews', 'Planning'] }
  },
  'ai-wallet': {
    name: { en: 'AI Wallet & Credits', hi: 'AI वॉलेट और क्रेडिट्स' },
    icon: '💰',
    category: 'admin',
    shortDesc: { en: 'Manage AI usage credits and billing', hi: 'AI usage credits और billing manage करें' },
    fullDesc: { en: 'Track and manage AI feature usage with a wallet-based credit system. Monitor usage per feature, set daily limits, and recharge credits as needed.', hi: 'Wallet-based credit system से AI feature usage track और manage करें। Per feature usage monitor करें, daily limits set करें, और जरूरत अनुसार credits recharge करें।' },
    howItWorks: { en: ['View credit balance', 'Monitor usage by feature', 'Set daily limits', 'Recharge when needed'], hi: ['Credit balance देखें', 'Feature-wise usage monitor करें', 'Daily limits set करें', 'जरूरत पर recharge करें'] },
    benefits: { en: ['Cost control', 'Usage transparency', 'Per-feature tracking', 'Easy recharge'], hi: ['Cost control', 'Usage transparency', 'Per-feature tracking', 'Easy recharge'] },
    limitations: { en: ['Credits required for AI features', 'Usage varies by feature'], hi: ['AI features के लिए credits जरूरी', 'Feature अनुसार usage vary करता है'] },
    useCases: { en: ['Budget planning', 'Usage monitoring', 'Cost optimization'], hi: ['Budget planning', 'Usage monitoring', 'Cost optimization'] }
  },
  'complete-package': {
    name: { en: 'Complete School Package', hi: 'Complete स्कूल पैकेज' },
    icon: '🎁',
    category: 'all',
    shortDesc: { en: 'All 31 features in one package', hi: 'एक पैकेज में सभी 31 features' },
    fullDesc: { en: 'Get access to all 31 AI-powered features in one comprehensive package. From admission to analytics, fee management to WhatsApp broadcasting - everything your school needs to go digital.', hi: 'एक comprehensive package में सभी 31 AI-powered features का access पाएं। Admission से analytics, fee management से WhatsApp broadcasting तक - आपके school को digital बनाने के लिए सब कुछ।' },
    howItWorks: { en: ['Subscribe to complete package', 'Get all 31 features activated', 'Dedicated onboarding support', 'Regular updates included'], hi: ['Complete package subscribe करें', 'सभी 31 features activate हों', 'Dedicated onboarding support', 'Regular updates included'] },
    benefits: { en: ['Best value for money', 'All features included', 'Priority support', 'Free updates'], hi: ['Best value for money', 'सभी features included', 'Priority support', 'Free updates'] },
    limitations: { en: ['Requires training for all features', 'Full staff adoption needed'], hi: ['सभी features के लिए training जरूरी', 'Full staff adoption जरूरी'] },
    useCases: { en: ['New school setup', 'Complete digitization', 'School chains'], hi: ['New school setup', 'Complete digitization', 'School chains'] }
  }
};

// AI Feature Detail Page Component
export function AIFeatureDetailPage({ featureId, onBack, token }) {
  const { language, t } = useLanguage();
  const [activeTab, setActiveTab] = useState('overview');
  const feature = featureDetails[featureId];
  
  if (!feature) {
    return (
      <div className="p-8 text-center">
        <p className="text-gray-500">{language === 'hi' ? 'सुविधा नहीं मिली' : 'Feature not found'}</p>
        <button onClick={onBack} className="mt-4 text-orange-600 hover:underline">
          {t('back')}
        </button>
      </div>
    );
  }

  const tabs = [
    { id: 'overview', label: language === 'hi' ? 'अवलोकन' : 'Overview' },
    { id: 'howItWorks', label: language === 'hi' ? 'कैसे काम करता है' : 'How it Works' },
    { id: 'benefits', label: language === 'hi' ? 'लाभ' : 'Benefits' },
    { id: 'limitations', label: language === 'hi' ? 'सीमाएं' : 'Limitations' },
    { id: 'useCases', label: language === 'hi' ? 'उपयोग' : 'Use Cases' },
  ];

  // Special section for Homework Hints with comic characters
  const isHomeworkHint = featureId === 'homework-hint';

  return (
    <div className="max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <button 
          onClick={onBack} 
          className="p-2 hover:bg-gray-100 rounded-full transition"
          data-testid="feature-detail-back"
        >
          <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <div className="flex items-center gap-3">
          <span className="text-4xl">{feature.icon}</span>
          <div>
            <h1 className="text-2xl font-bold text-gray-800">{feature.name[language]}</h1>
            <p className="text-gray-500">{feature.shortDesc[language]}</p>
          </div>
        </div>
      </div>

      {/* Category Badge */}
      <div className="mb-6">
        <span className={`px-3 py-1 rounded-full text-sm font-medium ${
          feature.category === 'admin' ? 'bg-purple-100 text-purple-700' :
          feature.category === 'teacher' ? 'bg-blue-100 text-blue-700' :
          feature.category === 'student' ? 'bg-green-100 text-green-700' :
          feature.category === 'parent' ? 'bg-orange-100 text-orange-700' :
          'bg-gray-100 text-gray-700'
        }`}>
          {feature.category === 'admin' ? (language === 'hi' ? 'एडमिन' : 'Admin') :
           feature.category === 'teacher' ? (language === 'hi' ? 'शिक्षक' : 'Teacher') :
           feature.category === 'student' ? (language === 'hi' ? 'विद्यार्थी' : 'Student') :
           feature.category === 'parent' ? (language === 'hi' ? 'अभिभावक' : 'Parent') :
           (language === 'hi' ? 'सभी' : 'All')}
        </span>
      </div>

      {/* Tabs */}
      <div className="flex border-b mb-6 overflow-x-auto">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-3 text-sm font-medium whitespace-nowrap transition-colors ${
              activeTab === tab.id 
                ? 'text-orange-600 border-b-2 border-orange-600' 
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="bg-white rounded-xl shadow-sm p-6">
        {activeTab === 'overview' && (
          <div className="space-y-4">
            <p className="text-gray-700 leading-relaxed">{feature.fullDesc[language]}</p>
            
            {/* Special Comic Characters Section for Homework Hints */}
            {isHomeworkHint && feature.comicCharacters && (
              <div className="mt-6 p-4 bg-gradient-to-r from-yellow-50 to-orange-50 rounded-xl border border-orange-200">
                <h3 className="font-bold text-orange-800 mb-4 flex items-center gap-2">
                  <span className="text-2xl">🦸</span>
                  {language === 'hi' ? 'कॉमिक कैरेक्टर्स' : 'Comic Characters'}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {Object.entries(feature.comicCharacters).map(([key, char]) => (
                    <div key={key} className="flex items-start gap-3 p-3 bg-white rounded-lg shadow-sm">
                      <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center text-2xl">
                        {key === 'chacha' ? '👴' : key === 'sabu' ? '💪' : key === 'billu' ? '👦' : '👧'}
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-800">{char.name}</h4>
                        <p className="text-sm text-gray-600">{char.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
                <p className="mt-4 text-sm text-orange-700 italic">
                  {language === 'hi' 
                    ? '* ये प्यारे कैरेक्टर होमवर्क को मज़ेदार कहानियों में बदल देते हैं!'
                    : '* These beloved characters turn homework into fun stories!'}
                </p>
              </div>
            )}
          </div>
        )}

        {activeTab === 'howItWorks' && (
          <div className="space-y-3">
            {feature.howItWorks[language].map((step, idx) => (
              <div key={idx} className="flex items-start gap-3">
                <span className="w-8 h-8 bg-orange-100 text-orange-700 rounded-full flex items-center justify-center font-bold text-sm shrink-0">
                  {idx + 1}
                </span>
                <p className="text-gray-700 pt-1">{step}</p>
              </div>
            ))}
          </div>
        )}

        {activeTab === 'benefits' && (
          <div className="space-y-3">
            {feature.benefits[language].map((benefit, idx) => (
              <div key={idx} className="flex items-start gap-3">
                <span className="text-green-500 mt-1">✓</span>
                <p className="text-gray-700">{benefit}</p>
              </div>
            ))}
          </div>
        )}

        {activeTab === 'limitations' && (
          <div className="space-y-3">
            {feature.limitations[language].map((limitation, idx) => (
              <div key={idx} className="flex items-start gap-3">
                <span className="text-orange-500 mt-1">⚠</span>
                <p className="text-gray-700">{limitation}</p>
              </div>
            ))}
          </div>
        )}

        {activeTab === 'useCases' && (
          <div className="flex flex-wrap gap-2">
            {feature.useCases[language].map((useCase, idx) => (
              <span key={idx} className="px-4 py-2 bg-gray-100 text-gray-700 rounded-full text-sm">
                {useCase}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Try Now Button */}
      <div className="mt-6 text-center">
        <button 
          onClick={onBack}
          className="px-8 py-3 bg-gradient-to-r from-orange-500 to-amber-500 text-white font-semibold rounded-lg hover:from-orange-600 hover:to-amber-600 transition shadow-lg"
          data-testid="try-feature-btn"
        >
          {language === 'hi' ? 'अभी इस्तेमाल करें' : 'Try Now'} →
        </button>
      </div>
    </div>
  );
}

// Export feature details for use in other components
export { featureDetails };

export default AIFeatureDetailPage;
